import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { prospects as prospectsApi, prospectGroups as prospectGroupsApi } from "../services/api";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2, FiSearch, FiUpload, FiDownload, FiEdit2, FiCheck, FiX, FiUserPlus, FiFilter, FiArrowRight, FiChevronUp, FiChevronDown, FiUsers } from "react-icons/fi";
import ColumnToggle from "../components/ColumnToggle";

// ═══════════════════════════════════════════════════════════════
// المحتملين — Prospects
// v3.3.276 — فلترة Excel-style + تحديد جماعي + حذف جماعي
// ═══════════════════════════════════════════════════════════════

const STATUS_MAP = {
  new: { label: "جديد", color: "#3b82f6", bg: "#dbeafe" },
  contacted: { label: "تم التواصل", color: "#f59e0b", bg: "#fef3c7" },
  converted: { label: "تم الترحيل", color: "#10b981", bg: "#dcfce7" },
  rejected: { label: "مرفوض", color: "#ef4444", bg: "#fee2e2" },
};

// v3.3.606 — تصنيف المحتمل
const CLASSIFICATIONS = ["عميل شقة", "مستثمر", "مطور", "مسوق", "تصنيف آخر"];
const CLASSIFICATION_STYLE = {
  "عميل شقة":   { bg: "#dbeafe", color: "#1e40af" },
  "مستثمر":     { bg: "#dcfce7", color: "#166534" },
  "مطور":       { bg: "#fef3c7", color: "#92400e" },
  "مسوق":       { bg: "#ede9fe", color: "#5b21b6" },
  "تصنيف آخر":  { bg: "#f1f5f9", color: "#475569" },
};

const thS = { padding: "6px 10px", fontSize: 11, fontWeight: 600, color: "#64748b", background: "#f8fafc", borderBottom: "2px solid #e2e8f0", textAlign: "right", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2, userSelect: "none" };
const tdS = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };
const inputS = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" };
const btnS = (bg, color, border) => ({ padding: "7px 14px", background: bg, color, border: border || "none", borderRadius: 7, fontFamily: "inherit", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 });

// ─── Sort Icon ───
function SortIcon({ sortKey, sortDir, colKey }) {
  if (sortKey !== colKey) return <span style={{ color: "#cbd5e1", fontSize: 10 }}>⇅</span>;
  return sortDir === "asc"
    ? <FiChevronUp size={12} style={{ color: "#2563eb" }} />
    : <FiChevronDown size={12} style={{ color: "#2563eb" }} />;
}

// ═══════════════════════════════════════════════════════════════
// Excel-style Column Filter Dropdown
// selected = Set of keys. Empty Set = "all" (no filter). Has("__NONE__") = nothing matches.
// ═══════════════════════════════════════════════════════════════
function ColumnFilterDropdown({ colKey, label, values, selected, onChange, onClose, onSort, sortKey, sortDir, canSort }) {
  const [search, setSearch] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const filteredValues = useMemo(() => {
    if (!search.trim()) return values;
    const q = search.trim().toLowerCase();
    return values.filter(v => String(v.label).toLowerCase().includes(q));
  }, [values, search]);

  const isChecked = (key) => {
    if (selected.size === 0) return true; // empty = all
    if (selected.has("__NONE__")) return false;
    return selected.has(key);
  };

  const toggleOne = (val) => {
    let next;
    if (selected.size === 0) {
      // currently "all" — switch to explicit set and uncheck this one
      next = new Set(values.map(v => v.key));
      next.delete(val);
    } else if (selected.has("__NONE__")) {
      // currently "none" — start with just this one
      next = new Set([val]);
    } else {
      next = new Set(selected);
      if (next.has(val)) next.delete(val);
      else next.add(val);
    }
    // Normalize
    if (next.size === values.length) onChange(new Set()); // back to "all"
    else if (next.size === 0) onChange(new Set(["__NONE__"]));
    else onChange(next);
  };

  const selectAll = () => {
    if (search.trim()) {
      // add all visible to current selection
      let next = selected.size === 0
        ? new Set(values.map(v => v.key))
        : selected.has("__NONE__") ? new Set() : new Set(selected);
      filteredValues.forEach(v => next.add(v.key));
      if (next.size === values.length) onChange(new Set());
      else if (next.size === 0) onChange(new Set(["__NONE__"]));
      else onChange(next);
    } else {
      onChange(new Set()); // all
    }
  };

  const clearAll = () => {
    if (search.trim()) {
      // remove only visible from selection
      let next = selected.size === 0
        ? new Set(values.map(v => v.key))
        : selected.has("__NONE__") ? new Set() : new Set(selected);
      filteredValues.forEach(v => next.delete(v.key));
      if (next.size === values.length) onChange(new Set());
      else if (next.size === 0) onChange(new Set(["__NONE__"]));
      else onChange(next);
    } else {
      onChange(new Set(["__NONE__"]));
    }
  };

  const selectedCount = selected.size === 0 ? values.length : selected.has("__NONE__") ? 0 : selected.size;

  return (
    <div ref={panelRef} style={{
      position: "absolute", top: "100%", right: 0, marginTop: 2,
      background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8,
      boxShadow: "0 10px 25px rgba(0,0,0,0.12)", zIndex: 100,
      width: 270, maxHeight: 420, display: "flex", flexDirection: "column",
      overflow: "hidden", fontWeight: 400, color: "#1e293b",
    }} onClick={e => e.stopPropagation()}>
      {/* Sort section */}
      {canSort && (
        <div style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", gap: 6 }}>
          <button onClick={() => { onSort(colKey, "asc"); onClose(); }}
            style={{ flex: 1, padding: "4px 8px", fontSize: 11, background: sortKey === colKey && sortDir === "asc" ? "#dbeafe" : "#fff", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", color: "#1e293b" }}>
            <FiChevronUp size={10} style={{ display: "inline", verticalAlign: "middle" }} /> تصاعدي
          </button>
          <button onClick={() => { onSort(colKey, "desc"); onClose(); }}
            style={{ flex: 1, padding: "4px 8px", fontSize: 11, background: sortKey === colKey && sortDir === "desc" ? "#dbeafe" : "#fff", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", color: "#1e293b" }}>
            <FiChevronDown size={10} style={{ display: "inline", verticalAlign: "middle" }} /> تنازلي
          </button>
        </div>
      )}

      {/* Search inside column */}
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ position: "relative" }}>
          <FiSearch size={11} style={{ position: "absolute", right: 7, top: 7, color: "#94a3b8" }} />
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`بحث في "${label}"...`}
            style={{ width: "100%", padding: "5px 24px 5px 8px", border: "1px solid #cbd5e1", borderRadius: 5, fontFamily: "inherit", fontSize: 11, outline: "none", boxSizing: "border-box" }}
          />
        </div>
      </div>

      {/* Select all / Clear */}
      <div style={{ padding: "4px 10px", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 6, fontSize: 11 }}>
        <button onClick={selectAll}
          style={{ flex: 1, padding: "3px 6px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>
          تحديد الكل{search.trim() ? " (الظاهر)" : ""}
        </button>
        <button onClick={clearAll}
          style={{ flex: 1, padding: "3px 6px", background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>
          إلغاء الكل{search.trim() ? " (الظاهر)" : ""}
        </button>
      </div>

      {/* Values list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 0", maxHeight: 240 }}>
        {filteredValues.length === 0 ? (
          <div style={{ padding: 12, textAlign: "center", color: "#94a3b8", fontSize: 11 }}>لا توجد قيم</div>
        ) : filteredValues.map(v => (
          <label key={v.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, color: "#334155" }}
            onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <input type="checkbox" checked={isChecked(v.key)} onChange={() => toggleOne(v.key)} style={{ cursor: "pointer" }} />
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.label}</span>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>{v.count}</span>
          </label>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "6px 10px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#64748b" }}>
          {selectedCount} من {values.length} محدد
        </span>
        <button onClick={onClose} style={{ padding: "3px 10px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>
          تم
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
// v3.4.283 — ألوان التصنيف
const CLS_STYLE = { developer: ["#e0f2fe", "#075985"], supplier: ["#ffedd5", "#9a3412"], marketer: ["#fce7f3", "#9d174d"], investor: ["#dcfce7", "#166534"], client: ["#f1f5f9", "#475569"] };
const CLS_LABEL = { developer: "مطور عقاري", supplier: "مورد / مقاول", marketer: "مسوق عقاري", investor: "مستثمر / صاحب أعمال", client: "عميل محتمل (غير مصنف)" };
// v3.4.310 — لون التصنيف من بياناته (نص = اللون، خلفية = اللون بشفافية)
const clsStyleOf = (p, classes) => { const c = (classes || []).find(x => x.key === p?.cls); const color = p?.cls_color || c?.color || CLS_STYLE[p?.cls]?.[1] || "#475569"; return [color + "1f", color]; };
// v3.4.310 — إدارة التصنيفات (الكلمات الدالة التي يُصنَّف على أساسها كل رقم)
function ClassesManager({ classes, onChanged }) {
  const [edit, setEdit] = useState(null); const [busy, setBusy] = useState(false);
  const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit", fontSize: 13, boxSizing: "border-box" };
  const COLORS = ["#075985", "#9a3412", "#9d174d", "#166534", "#7c3aed", "#0f766e", "#b45309", "#be123c", "#1d4ed8", "#475569"];
  const save = async () => {
    if (!edit.name.trim()) return toast.error("اسم التصنيف مطلوب");
    setBusy(true);
    try { const body = { name: edit.name.trim(), color: edit.color, keywords: edit.keywords, priority: Number(edit.priority) || 100, is_active: edit.is_active !== false }; if (edit.id) await prospectsApi.updateClass(edit.id, body); else await prospectsApi.createClass(body); toast.success("حُفظ — يُعاد التصنيف الآن"); setEdit(null); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  const del = async (c) => { if (!window.confirm(`حذف تصنيف «${c.name}»؟ ستعود أرقامه إلى التصنيف الافتراضي أو التصنيف التالي المطابق`)) return; try { await prospectsApi.deleteClass(c.id); toast.success("حُذف"); onChanged(); } catch (e) { toast.error(e.response?.data?.error || "فشل الحذف"); } };
  const move = async (c, dir) => { const list = classes.filter(x => !x.is_default).sort((a, b) => a.priority - b.priority || a.id - b.id); const i = list.findIndex(x => x.id === c.id); const j = i + dir; if (j < 0 || j >= list.length) return; try { await prospectsApi.updateClass(list[i].id, { priority: list[j].priority === list[i].priority ? list[j].priority + dir : list[j].priority }); await prospectsApi.updateClass(list[j].id, { priority: list[i].priority }); onChanged(); } catch (e) { toast.error("فشل الترتيب"); } };
  const sorted = [...classes].sort((a, b) => (a.is_default - b.is_default) || a.priority - b.priority || a.id - b.id);
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div><div style={{ fontSize: 14, fontWeight: 800 }}>🏷️ التصنيفات</div><div style={{ fontSize: 11.5, color: "#64748b" }}>يظهر لكل رقم كل التصنيفات التي تطابق كلماتها اسمَه أو أسماءه في النمبربوك (الأول بحسب الترتيب هو الأساسي) — غير المطابق يذهب للتصنيف الافتراضي. «التصنيف النهائي» يُؤكَّد يدويًا من الجداول ولا يتأثر بإعادة التصنيف. الكلمات تُفصل بفاصلة أو سطر جديد.</div></div>
        <button onClick={() => setEdit({ name: "", color: COLORS[classes.length % COLORS.length], keywords: [], priority: (Math.max(0, ...classes.filter(c => !c.is_default).map(c => c.priority)) + 10), is_active: true })} style={{ marginInlineStart: "auto", padding: "7px 14px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>+ تصنيف جديد</button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["الترتيب", "التصنيف", "الكلمات الدالة", "الأرقام (آلي)", "نهائي مؤكد", "الحالة", ""].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, fontWeight: 800, color: "#475569", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>{h}</th>)}</tr></thead>
        <tbody>{sorted.map((c, i) => (
          <tr key={c.id} style={{ opacity: c.is_active === false ? 0.55 : 1 }}>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{c.is_default ? <span style={{ fontSize: 10.5, color: "#94a3b8" }}>الأخير (افتراضي)</span> : <><span style={{ fontFamily: "monospace", fontWeight: 800 }}>{i + 1}</span> <button onClick={() => move(c, -1)} style={{ border: "1px solid #e2e8f0", background: "#fff", borderRadius: 4, cursor: "pointer", fontSize: 10, padding: "0 5px" }}>▲</button><button onClick={() => move(c, 1)} style={{ border: "1px solid #e2e8f0", background: "#fff", borderRadius: 4, cursor: "pointer", fontSize: 10, padding: "0 5px" }}>▼</button></>}</td>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9" }}><span style={{ background: (c.color || "#475569") + "1f", color: c.color || "#475569", borderRadius: 999, padding: "2px 10px", fontWeight: 800, fontSize: 12 }}>{c.name}</span><div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace" }}>{c.key}</div></td>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", maxWidth: 520 }}>{c.is_default ? <span style={{ color: "#94a3b8", fontSize: 11.5 }}>كل ما لم يطابق أي تصنيف</span> : <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{(c.keywords || []).slice(0, 25).map((k, j) => <span key={j} style={{ background: "#f1f5f9", borderRadius: 6, padding: "1px 6px", fontSize: 10.5 }}>{k}</span>)}{(c.keywords || []).length > 25 && <span style={{ fontSize: 10.5, color: "#94a3b8" }}>+{c.keywords.length - 25}</span>}</div>}</td>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", fontFamily: "monospace", fontWeight: 800 }}>{(Number(c.count) || 0).toLocaleString("en-US")}<span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}> (أساسي {(Number(c.primary_count) || 0).toLocaleString("en-US")})</span></td>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", fontFamily: "monospace", fontWeight: 900, color: "#166534" }}>{(Number(c.final_count) || 0).toLocaleString("en-US")}</td>{/* v3.4.324 */}
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", fontSize: 11 }}>{c.is_active === false ? "موقوف" : "فعّال"}</td>
            <td style={{ padding: "7px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
              <button onClick={() => setEdit({ id: c.id, name: c.name, color: c.color || "#475569", keywords: c.keywords || [], priority: c.priority, is_active: c.is_active !== false, is_default: c.is_default })} style={{ border: "1px solid #e2e8f0", background: "#fff", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 800 }}>✎ تعديل</button>
              {!c.is_default && <button onClick={() => del(c)} style={{ border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, marginInlineStart: 4 }}>🗑</button>}
            </td>
          </tr>))}</tbody>
      </table>
      {edit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => !busy && setEdit(null)}>
          <div dir="rtl" onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, width: 620, maxWidth: "100%", maxHeight: "92vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>{edit.id ? "✎ تعديل التصنيف" : "+ تصنيف جديد"}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
              <div><label style={{ fontSize: 11.5, fontWeight: 700, color: "#475569" }}>اسم التصنيف</label><input value={edit.name} onChange={e => setEdit(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="مثال: مكاتب عقارية" /></div>
              <div><label style={{ fontSize: 11.5, fontWeight: 700, color: "#475569" }}>اللون</label><div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>{COLORS.map(c => <button key={c} onClick={() => setEdit(f => ({ ...f, color: c }))} style={{ width: 22, height: 22, borderRadius: "50%", background: c, border: edit.color === c ? "3px solid #0f172a" : "2px solid #fff", cursor: "pointer" }} />)}</div></div>
            </div>
            {!edit.is_default && <div style={{ marginTop: 8 }}><label style={{ fontSize: 11.5, fontWeight: 700, color: "#475569" }}>الكلمات الدالة ({(Array.isArray(edit.keywords) ? edit.keywords : String(edit.keywords || "").split(/[\n,،]/)).filter(x => String(x).trim()).length}) — تُفصل بفاصلة أو سطر جديد</label>
              <textarea value={Array.isArray(edit.keywords) ? edit.keywords.join("، ") : edit.keywords} onChange={e => setEdit(f => ({ ...f, keywords: e.target.value }))} rows={8} style={{ ...inp, resize: "vertical", lineHeight: 1.8 }} placeholder="مثال: مكتب عقار، مكتب العقار، وساطة، سمسار" /></div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              {!edit.is_default && <div><label style={{ fontSize: 11.5, fontWeight: 700, color: "#475569" }}>الأولوية (الأقل أولًا)</label><input type="number" value={edit.priority} onChange={e => setEdit(f => ({ ...f, priority: e.target.value }))} style={inp} /></div>}
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, cursor: "pointer", marginTop: 18 }}><input type="checkbox" checked={edit.is_active !== false} onChange={e => setEdit(f => ({ ...f, is_active: e.target.checked }))} disabled={edit.is_default} /> فعّال</label>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={save} disabled={busy} style={{ flex: 1, padding: 10, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>{busy ? "…" : "💾 حفظ وإعادة التصنيف"}</button>
              <button onClick={() => setEdit(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f1f5f9", cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// v3.4.278 — تحليل قائمة المسوقين المحتملين
function ProspectsAnalysis({ onOpen }) {
  const [exGroup, setExGroup] = useState(null); // v3.4.283
  const [exCls, setExCls] = useState("");
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  const [sec, setSec] = useState("overview");
  const [q, setQ] = useState("");
  const load = useCallback(() => { setErr(""); prospectsApi.analysis().then(r => setD(r.data)).catch(e => setErr(e.response?.data?.error || e.message)); }, []);
  // v3.4.324 — التصنيف النهائي: طبقة محلية فوق بيانات التحليل حتى لا نعيد التحميل بعد كل تأكيد
  const [finalMap, setFinalMap] = useState({});
  const setFinal = async (p, cls) => {
    try { await prospectsApi.setFinalClass(p.id, cls); setFinalMap(m => ({ ...m, [p.id]: cls || "" })); toast.success(cls ? "أُكِّد التصنيف النهائي" : "أُلغي التصنيف النهائي"); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };
  const finalOf = (p) => (Object.prototype.hasOwnProperty.call(finalMap, p.id) ? finalMap[p.id] : p.final_cls) || "";
  useEffect(() => { load(); }, [load]);
  const th = { padding: "8px 10px", textAlign: "right", fontSize: 11, fontWeight: 800, color: "#475569", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
  const td = { padding: "7px 10px", fontSize: 12.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };
  const Card = ({ l, v, c = "#1d4ed8", bg = "#eff6ff", sub }) => <div style={{ background: bg, border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 16px", minWidth: 160, flex: 1 }}><div style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>{l}</div><div style={{ fontSize: 24, fontWeight: 900, color: c, fontFamily: "monospace" }}>{v}</div>{sub && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{sub}</div>}</div>;
  const Box = ({ title, sub, children }) => <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 14 }}><div style={{ fontSize: 14, fontWeight: 800, marginBottom: 2 }}>{title}</div>{sub && <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>{sub}</div>}{children}</div>;
  const num = (n) => (Number(n) || 0).toLocaleString("en-US");
  const PeopleTable = ({ rows, hits }) => {
    const f = q.trim() ? rows.filter(p => [p.name, p.phone, ...(p.nbs || []), ...(p.groups || [])].some(x => String(x || "").includes(q.trim()))) : rows;
    return (
      <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["#", "الاسم", "أسماء النمبربوك", "التصنيف الآلي", "التصنيف النهائي ✓", "الجوال", "الجروبات", "من حفظه", ...(hits ? ["الكلمات الدالة"] : []), "أمثلة الجروبات", ""].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>{/* v3.4.282 — عمود النمبربوك مستقل — v3.4.283 التصنيف */}
          <tbody>
            {f.length === 0 && <tr><td colSpan={11} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: 18 }}>لا نتائج</td></tr>}
            {f.map((p, i) => (
              <tr key={p.id}>
                <td style={{ ...td, color: "#94a3b8" }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 800 }}>{p.name || "—"}</td>
                <td style={{ ...td, maxWidth: 280 }}><div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{(p.nbs || []).map((x, j) => <span key={j} style={{ background: "#fef9c3", color: "#713f12", border: "1px solid #fde68a", borderRadius: 6, padding: "1px 6px", fontSize: 10.5 }}>{x}</span>)}{(p.nbs || []).length === 0 && <span style={{ color: "#cbd5e1" }}>—</span>}</div></td>{/* v3.4.282 */}
                <td style={td}>{(p.cls_all && p.cls_all.length ? p.cls_all : (p.cls ? [{ cls: p.cls, label: p.cls_label, color: p.cls_color }] : [])).length ? <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{(p.cls_all && p.cls_all.length ? p.cls_all : [{ cls: p.cls, label: p.cls_label, color: p.cls_color }]).map((c, j) => { const st = clsStyleOf(c, d.classes); return <span key={j} title={c.hit ? "الكلمة الدالة: " + c.hit : ""} style={{ background: st[0], color: st[1], borderRadius: 999, padding: "2px 9px", fontWeight: 800, fontSize: 11, whiteSpace: "nowrap", border: j === 0 ? "1.5px solid " + st[1] : "1px solid transparent" }}>{c.label}</span>; })}</div> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>{/* v3.4.283 — v3.4.310 — v3.4.324: كل التصنيفات المطابقة */}
                <td style={td}>{(() => { const fin = finalOf(p); const fc = (d.classes || []).find(c => c.key === fin); return fin && fc ? (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ background: (fc.color || "#166534") + "1f", color: fc.color || "#166534", borderRadius: 999, padding: "2px 9px", fontWeight: 900, fontSize: 11, whiteSpace: "nowrap", border: "1.5px solid " + (fc.color || "#166534") }}>✓ {fc.name}</span>
                    <select value={fin} onChange={e => setFinal(p, e.target.value)} title="تغيير التصنيف النهائي" style={{ padding: "2px 4px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 10.5, background: "#fff", maxWidth: 24, color: "transparent" }}>
                      <option value="">— إلغاء —</option>{(d.classes || []).map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                    </select>
                  </div>) : (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {p.cls && <button onClick={() => setFinal(p, p.cls)} title={"تأكيد التصنيف الآلي: " + (p.cls_label || "")} style={{ border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>✓ تأكيد</button>}
                    <select value="" onChange={e => e.target.value && setFinal(p, e.target.value)} style={{ padding: "2px 6px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 10.5, background: "#fff", maxWidth: 130 }}>
                      <option value="">تصنيف آخر…</option>{(d.classes || []).map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                    </select>
                  </div>); })()}</td>{/* v3.4.324 — التصنيف النهائي المدقَّق */}
                <td style={{ ...td, fontFamily: "monospace", direction: "ltr", textAlign: "right" }}>{p.phone}</td>
                <td style={td}><span style={{ background: "#eff6ff", color: "#1e40af", borderRadius: 999, padding: "1px 8px", fontWeight: 800, fontSize: 11 }}>{p.groups_count}</span></td>
                <td style={td}><span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 999, padding: "1px 8px", fontWeight: 800, fontSize: 11 }}>{p.nb_count}</span></td>
                {hits && <td style={{ ...td, maxWidth: 200 }}><div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{(p.hits || []).map((x, j) => <span key={j} style={{ background: "#dcfce7", color: "#166534", borderRadius: 6, padding: "1px 6px", fontSize: 10.5, fontWeight: 700 }}>{x}</span>)}</div></td>}
                <td style={{ ...td, maxWidth: 260, fontSize: 11, color: "#64748b" }}>{(p.groups || []).join("، ")}</td>
                <td style={td}><button onClick={() => onOpen(p.phone)} style={{ border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e40af", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 800 }}>فتح في القائمة ›</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  if (err) return <div style={{ color: "#b91c1c", padding: 20 }}>تعذر التحليل: {err}</div>;
  if (!d) return <div style={{ color: "#94a3b8", padding: 30, textAlign: "center" }}>جاري تحليل {""}القائمة… (قد يستغرق ثوانٍ)</div>;
  const CLASSES = d.classes || []; // v3.4.310
  const SECS = [["overview", "📈 نظرة عامة"], ["present", "🏆 الأكثر تواجدًا"], ["groups", "👥 الجروبات والتشابه"], ["exclusive", "🎯 الحصريون"], ...CLASSES.filter(c => !c.is_default && c.is_active !== false).map(c => [c.key, "🏷️ " + c.name]), ["classes", "⚙️ التصنيفات"]];
  const maxC = Math.max(1, ...(d.countries || []).map(c => c.n));
  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        {SECS.map(([k, l]) => <button key={k} onClick={() => setSec(k)} style={{ padding: "6px 12px", borderRadius: 999, border: "1.5px solid " + (sec === k ? "#2563eb" : "#e2e8f0"), background: sec === k ? "#eff6ff" : "#fff", color: sec === k ? "#1d4ed8" : "#475569", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>{l}{d.lists?.[k] ? ` (${num(d.lists[k].count)})` : ""}</button>)}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 تصفية النتائج…" style={{ marginInlineStart: "auto", padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit", fontSize: 12, minWidth: 200 }} />
      </div>
      {sec === "overview" && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Card l="إجمالي الأرقام" v={num(d.total)} />
            <Card l="عدد الجروبات" v={num(d.groups_total)} c="#7c3aed" bg="#f5f3ff" />
            <Card l="متوسط الجروبات لكل رقم" v={d.avg_groups} c="#0f766e" bg="#f0fdfa" />
            <Card l="أرقام في أكثر من جروب" v={num(d.multi_group)} c="#b45309" bg="#fffbeb" sub={`${Math.round(100 * d.multi_group / Math.max(1, d.total))}% من القائمة`} />
            {CLASSES.filter(c => c.is_active !== false).map(c => <Card key={c.key} l={c.name} v={num(c.count)} sub={`أساسي ${num(c.primary_count)} · نهائي مؤكد ${num(c.final_count)}`} c={c.color} bg={c.color + "14"} />)}{/* v3.4.310 — v3.4.324: أساسي/نهائي */}
            <Card l="✓ تصنيفات نهائية مدقَّقة" v={num(d.final_total)} sub={`${Math.round(100 * (d.final_total || 0) / Math.max(1, d.total))}% من القائمة · ${num(d.multi_count)} رقم بأكثر من تصنيف آلي`} c="#166534" bg="#f0fdf4" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
            <Box title="🌍 توزيع الدول (حسب مفتاح الدولة)">
              {(d.countries || []).slice(0, 12).map(c => <div key={c.country} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, fontSize: 12 }}><span style={{ width: 90, fontWeight: 700 }}>{c.country}</span><div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 12 }}><div style={{ width: `${Math.round(100 * c.n / maxC)}%`, background: "#3b82f6", height: "100%", borderRadius: 6 }} /></div><span style={{ fontFamily: "monospace", width: 60, textAlign: "left" }}>{num(c.n)}</span></div>)}
            </Box>
            <Box title="🔢 توزيع عدد الجروبات لكل رقم" sub="كلما زاد عدد الجروبات زادت فرصة أن يكون الرقم مسوقًا نشطًا أو مطورًا">
              {Object.entries(d.dist).map(([k, v]) => <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, fontSize: 12 }}><span style={{ width: 90, fontWeight: 700 }}>{k} {k === "1" ? "جروب" : "جروبات"}</span><div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 12 }}><div style={{ width: `${Math.round(100 * v / Math.max(1, d.total))}%`, background: "#8b5cf6", height: "100%", borderRadius: 6 }} /></div><span style={{ fontFamily: "monospace", width: 60, textAlign: "left" }}>{num(v)}</span></div>)}
            </Box>
            <Box title="🏅 أكبر الجروبات" sub="الأعضاء وحصة كل جروب من القائمة">
              <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["الجروب", "الأعضاء", "الحصة"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead><tbody>{(d.groups || []).slice(0, 10).map(g => <tr key={g.name}><td style={td}>{g.name}</td><td style={{ ...td, fontFamily: "monospace" }}>{num(g.members)}</td><td style={{ ...td, fontFamily: "monospace" }}>{g.share_pct}%</td></tr>)}</tbody></table>
            </Box>
          </div>
        </>
      )}
      {sec === "present" && (
        <>
          <Box title="🏆 الأكثر تواجدًا في الجروبات" sub="الأرقام الموجودة في أكبر عدد من الجروبات — غالبًا مسوقون نشطون أو جهات تسويقية"><PeopleTable rows={d.top_present} /></Box>
          <Box title="⭐ الأكثر شهرة" sub="الأرقام التي حفظها أكبر عدد من الناس في النمبربوك (عدد الأسماء المسجلة للرقم)"><PeopleTable rows={d.most_named} /></Box>
        </>
      )}
      {sec === "groups" && (
        <>
          <Box title="👥 الجروبات" sub="الحصرية = نسبة الأعضاء غير الموجودين في أي جروب آخر — كلما ارتفعت كان الجروب مختلفًا عن البقية">
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["الجروب", "الأعضاء", "الحصة من القائمة", "حصريون", "الحصرية %", "متوسط جروبات العضو"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>{(d.groups || []).filter(g => !q.trim() || g.name.includes(q.trim())).map(g => <tr key={g.name}><td style={{ ...td, fontWeight: 700 }}>{g.name}</td><td style={{ ...td, fontFamily: "monospace" }}>{num(g.members)}</td><td style={{ ...td, fontFamily: "monospace" }}>{g.share_pct}%</td><td style={{ ...td, fontFamily: "monospace" }}>{num(g.exclusive)}</td><td style={{ ...td, fontFamily: "monospace", color: g.exclusive_pct >= 50 ? "#166534" : "#475569", fontWeight: 700 }}>{g.exclusive_pct}%</td><td style={{ ...td, fontFamily: "monospace" }}>{g.avg_groups}</td></tr>)}</tbody></table></div>
          </Box>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 12 }}>
            <Box title="🔗 أكثر الجروبات تشابهًا" sub="نسبة الأعضاء المشتركين (Jaccard) — نفس الجمهور تقريبًا">
              <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["الجروب أ", "الجروب ب", "مشترك", "تشابه"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead><tbody>{(d.most_similar || []).map((p, i) => <tr key={i}><td style={td}>{p.a} <span style={{ color: "#94a3b8", fontSize: 10.5 }}>({num(p.a_members)})</span></td><td style={td}>{p.b} <span style={{ color: "#94a3b8", fontSize: 10.5 }}>({num(p.b_members)})</span></td><td style={{ ...td, fontFamily: "monospace" }}>{num(p.common)}</td><td style={{ ...td, fontFamily: "monospace", fontWeight: 800, color: "#1d4ed8" }}>{p.jaccard}%</td></tr>)}</tbody></table>
            </Box>
            <Box title="🧩 أكثر الجروبات اختلافًا" sub="جروبات معظم أعضائها غير موجودين في غيرها — جمهور جديد">
              <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["الجروب", "الأعضاء", "حصريون", "الحصرية"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead><tbody>{(d.most_distinct || []).map(g => <tr key={g.name}><td style={td}>{g.name}</td><td style={{ ...td, fontFamily: "monospace" }}>{num(g.members)}</td><td style={{ ...td, fontFamily: "monospace" }}>{num(g.exclusive)}</td><td style={{ ...td, fontFamily: "monospace", fontWeight: 800, color: "#166534" }}>{g.exclusive_pct}%</td></tr>)}</tbody></table>
              {(d.least_similar || []).length > 0 && <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 8 }}>أزواج بلا أي عضو مشترك: {d.least_similar.slice(0, 6).map(p => `${p.a} × ${p.b}`).join(" · ")}</div>}
            </Box>
          </div>
        </>
      )}
      {sec === "exclusive" && (() => { /* v3.4.283 */
        const ex = d.exclusives || []; const sum = d.exclusives_summary || {};
        const cur = ex.find(g => g.name === exGroup) || null;
        const allItems = cur ? cur.items : ex.flatMap(g => g.items.map(it => ({ ...it, groups: [g.name] })));
        const items = allItems.filter(it => !exCls || it.cls === exCls);
        return (
          <>
            <Box title="🎯 الحصريون — أرقام موجودة في جروب واحد فقط" sub="جمهور لا يتكرر في الجروبات الأخرى — مصنَّف آليًا من أسماء النمبربوك؛ غير المطابق لأي كلمة يُعدّ عميلًا محتملًا">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <Card l="إجمالي الحصريين" v={num(sum.total)} />
                {CLASSES.map(c => <Card key={c.key} l={c.name} v={num(sum[c.key])} c={c.color} bg={c.color + "14"} />)}{/* v3.4.310 */}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                <button onClick={() => setExGroup(null)} style={{ padding: "4px 10px", borderRadius: 999, border: "1.5px solid " + (!exGroup ? "#2563eb" : "#e2e8f0"), background: !exGroup ? "#eff6ff" : "#fff", color: !exGroup ? "#1d4ed8" : "#475569", fontWeight: 800, fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>كل الجروبات ({num(sum.total)})</button>
                {ex.map(g => <button key={g.name} onClick={() => setExGroup(g.name)} title={`الأعضاء ${num(g.members)} — الحصريون ${num(g.exclusive)}`} style={{ padding: "4px 10px", borderRadius: 999, border: "1.5px solid " + (exGroup === g.name ? "#2563eb" : "#e2e8f0"), background: exGroup === g.name ? "#eff6ff" : "#fff", color: exGroup === g.name ? "#1d4ed8" : "#475569", fontWeight: 700, fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>{g.name} <span style={{ fontFamily: "monospace" }}>({num(g.exclusive)})</span></button>)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                <button onClick={() => setExCls("")} style={{ padding: "3px 10px", borderRadius: 999, border: "1px solid " + (!exCls ? "#0f766e" : "#e2e8f0"), background: !exCls ? "#f0fdfa" : "#fff", color: "#0f766e", fontWeight: 800, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>كل التصنيفات</button>
                {CLASSES.map(c => <button key={c.key} onClick={() => setExCls(c.key)} style={{ padding: "3px 10px", borderRadius: 999, border: "1px solid " + (exCls === c.key ? c.color : "#e2e8f0"), background: exCls === c.key ? c.color + "1f" : "#fff", color: c.color, fontWeight: 800, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>{c.name} ({num(cur ? cur.by?.[c.key] : sum[c.key])})</button>)}{/* v3.4.310 */}
              </div>
              {cur && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>{cur.name}: {num(cur.members)} عضوًا — منهم {num(cur.exclusive)} حصريون ({Math.round(100 * cur.exclusive / Math.max(1, cur.members))}%)</div>}
              <PeopleTable rows={items} hits />
              {!cur && items.length > 300 && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>يُعرض حتى 300 صف لكل جروب — اختر جروبًا لعرض حصرييه كاملين.</div>}
            </Box>
          </>
        );
      })()}
      {d.lists?.[sec] && sec !== "overview" && (
        <Box title={(SECS.find(x => x[0] === sec) || ["", d.lists[sec].label])[1] + ` — ${num(d.lists[sec].count)}`} sub="من طابقت أسماؤهم أو أسماؤهم في النمبربوك الكلمات الدالة لهذا التصنيف — مرتبين بعدد الجروبات ثم من حفظه (يُعرض حتى 150)">
          <PeopleTable rows={d.lists[sec].items} hits />
        </Box>
      )}
      {sec === "classes" && <ClassesManager classes={CLASSES} onChanged={() => { setD(null); load(); }} />}{/* v3.4.310 */}
    </div>
  );
}

export default function ProspectsPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating);
  // v3.3.602 — gate add/export/import on the prospects module permissions.
  // Admin bypasses ONLY when not previewing a profile (so preview reflects perms).
  const adminFull = isAdmin && !user?.impersonating;
  const pp = user?.module_perms?.prospects || {};
  const canAdd    = adminFull || !!pp.can_add;
  const canExport = adminFull || !!pp.can_export;
  const canImport = adminFull || !!pp.can_import;

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", phone2: "", source: "", notes: "", classification: "" });
  const [editId, setEditId] = useState(null);     // v3.3.607 — now drives the edit POPUP (double-click)
  const [editData, setEditData] = useState({});
  const [convertingId, setConvertingId] = useState(null);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  // ─── Filter & Sort state ───
  const [globalSearch, setGlobalSearch] = useState("");
  const [pageTab, setPageTab] = useState("list"); // v3.4.278 — list | analysis
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterCol, setOpenFilterCol] = useState(null);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // ─── Selection state ───
  const [selectedIds, setSelectedIds] = useState(new Set());

  // v3.3.550 — Bulk add to prospect group state
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addToGroupBusy, setAddToGroupBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await prospectsApi.list({});
      setAllItems(res.data.prospects || []);
    } catch (err) {
      toast.error("خطأ في تحميل البيانات");
    }
    setLoading(false);
  }, []);

  const loadSources = async () => {
    try { const res = await prospectsApi.getSources(); setSources(res.data || []); } catch {}
  };

  const loadStats = async () => {
    try { const res = await prospectsApi.getStats(); setStats(res.data); } catch {}
  };

  useEffect(() => { load(); loadSources(); loadStats(); }, [load]);

  // ─── Extract displayable values for a column from a single item ───
  const getColumnValues = useCallback((key, item) => {
    if (key === "status") return [STATUS_MAP[item.status]?.label || item.status || ""];
    if (key === "group_count") return [String(item.group_count || 0)];
    if (key === "group_names") return (item.group_names || "").split("||").map(s => s.trim()).filter(Boolean);
    if (key === "nb_names") return (item.nb_names || "").split("||").map(s => s.trim()).filter(Boolean);
    // v3.3.550 — my_groups is a JSON array of {id, name} (membership in prospect_groups)
    if (key === "my_groups") {
      const arr = Array.isArray(item.my_groups) ? item.my_groups : [];
      return arr.map(g => g.name).filter(Boolean);
    }
    if (key === "created_at") return [item.created_at ? new Date(item.created_at).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" }) : ""];
    const v = item[key];
    return [v === null || v === undefined ? "" : String(v)];
  }, []);

  // ─── Check if an item matches a column filter ───
  const itemMatchesColFilter = useCallback((item, colKey, sel) => {
    if (!sel || sel.size === 0) return true;
    if (sel.has("__NONE__")) return false;
    const vals = getColumnValues(colKey, item);
    if (vals.length === 0) return sel.has("(فارغ)");
    // multi-value (group_names, nb_names, my_groups): match if ANY value is in selected
    return vals.some(v => sel.has(v || "(فارغ)"));
  }, [getColumnValues]);

  // ─── Global search match ───
  const itemMatchesGlobalSearch = useCallback((item) => {
    if (!globalSearch.trim()) return true;
    const q = globalSearch.trim().toLowerCase();
    const statusLabel = STATUS_MAP[item.status]?.label || item.status || "";
    const groupNames = (item.group_names || "").replace(/\|\|/g, " ");
    const nbNames = (item.nb_names || "").replace(/\|\|/g, " ");
    // v3.3.550 — search prospect-group names too
    const myGroupsStr = Array.isArray(item.my_groups) ? item.my_groups.map(g => g.name).join(" ") : "";
    const searchStr = [item.name, item.phone, item.phone2, item.source, statusLabel, item.notes, groupNames, nbNames, myGroupsStr, item.added_by_name, String(item.group_count || 0)].filter(Boolean).join(" ").toLowerCase();
    return searchStr.includes(q);
  }, [globalSearch]);

  // ─── Unique values per column (respects other filters, Excel-style) ───
  const getUniqueValuesForColumn = useCallback((colKey) => {
    const items = allItems.filter(item => {
      if (!itemMatchesGlobalSearch(item)) return false;
      for (const [k, sel] of Object.entries(columnFilters)) {
        if (k === colKey) continue; // exclude current column
        if (!itemMatchesColFilter(item, k, sel)) return false;
      }
      return true;
    });

    const counts = new Map();
    items.forEach(item => {
      const vals = getColumnValues(colKey, item);
      if (vals.length === 0) {
        counts.set("(فارغ)", (counts.get("(فارغ)") || 0) + 1);
      } else {
        vals.forEach(v => {
          const key = v || "(فارغ)";
          counts.set(key, (counts.get(key) || 0) + 1);
        });
      }
    });

    const entries = Array.from(counts.entries());
    if (colKey === "group_count") entries.sort((a, b) => Number(a[0]) - Number(b[0]));
    else entries.sort((a, b) => String(a[0]).localeCompare(String(b[0]), "ar"));
    return entries.map(([key, count]) => ({ key, label: key, count }));
  }, [allItems, columnFilters, itemMatchesGlobalSearch, itemMatchesColFilter, getColumnValues]);

  // ─── Filtered + sorted items ───
  const filteredItems = useMemo(() => {
    let items = allItems.filter(item => {
      if (!itemMatchesGlobalSearch(item)) return false;
      for (const [k, sel] of Object.entries(columnFilters)) {
        if (!itemMatchesColFilter(item, k, sel)) return false;
      }
      return true;
    });

    if (sortKey) {
      items = [...items].sort((a, b) => {
        let av = a[sortKey] ?? "";
        let bv = b[sortKey] ?? "";
        if (sortKey === "group_count") return sortDir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
        if (sortKey === "created_at") {
          const da = new Date(av).getTime() || 0, db = new Date(bv).getTime() || 0;
          return sortDir === "asc" ? da - db : db - da;
        }
        if (sortKey === "status") { av = STATUS_MAP[av]?.label || String(av); bv = STATUS_MAP[bv]?.label || String(bv); }
        const cmp = String(av).localeCompare(String(bv), "ar");
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return items;
  }, [allItems, globalSearch, columnFilters, sortKey, sortDir, itemMatchesGlobalSearch, itemMatchesColFilter]);

  const total = allItems.length;
  const filteredCount = filteredItems.length;
  // v3.4.190 — عرض على صفحات (200 صف) بدل رسم كل الصفوف دفعة واحدة؛ الفلاتر والفرز والتحديد تبقى على كل البيانات
  const PAGE = 200;
  const [pageNo, setPageNo] = useState(1);
  const pageCount = Math.max(1, Math.ceil(filteredCount / PAGE));
  useEffect(() => { setPageNo(1); }, [filteredCount]);
  const pageOffset = (Math.min(pageNo, pageCount) - 1) * PAGE;
  const pagedItems = useMemo(() => filteredItems.slice(pageOffset, pageOffset + PAGE), [filteredItems, pageOffset]);

  const handleSort = (key, dir) => { setSortKey(key); setSortDir(dir); };
  const clearAllFilters = () => { setColumnFilters({}); setGlobalSearch(""); };
  const hasActiveFilters = globalSearch.trim() || Object.values(columnFilters).some(s => s && s.size > 0);
  const colHasFilter = (key) => columnFilters[key] && columnFilters[key].size > 0;

  // ─── Selection ───
  const visibleIds = useMemo(() => filteredItems.map(i => i.id), [filteredItems]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAllVisible = () => {
    const next = new Set(selectedIds);
    if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
    else visibleIds.forEach(id => next.add(id));
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`حذف ${selectedIds.size} محتمل؟ لا يمكن التراجع.`)) return;
    try {
      const res = await prospectsApi.bulkDelete(Array.from(selectedIds));
      toast.success(`تم حذف ${res.data.deleted || selectedIds.size} ✅`);
      clearSelection();
      load(); loadStats();
    } catch { toast.error("فشل الحذف الجماعي"); }
  };

  // ─── CRUD ───
  const handleAdd = async () => {
    if (!form.name && !form.phone) return toast.error("الاسم أو الرقم مطلوب");
    try {
      await prospectsApi.add(form);
      toast.success("تمت الإضافة ✅");
      setForm({ name: "", phone: "", phone2: "", source: "", notes: "", classification: "" });
      setShowForm(false);
      load(); loadSources(); loadStats();
    } catch { toast.error("فشل الإضافة"); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditData({ name: item.name, phone: item.phone, phone2: item.phone2 || "", source: item.source || "", notes: item.notes || "", status: item.status, nb_names: item.nb_names || "", classification: item.classification || "" });
  };

  const saveEdit = async () => {
    try {
      await prospectsApi.update(editId, editData);
      toast.success("تم التحديث");
      setEditId(null); load(); loadSources();
    } catch { toast.error("فشل التحديث"); }
  };


  const handleDelete = async (id) => {
    if (!window.confirm("حذف هذا المحتمل؟")) return;
    try { await prospectsApi.delete(id); toast.success("تم الحذف"); load(); loadStats(); }
    catch { toast.error("فشل الحذف"); }
  };

  const handleConvert = async (id, convertTo) => {
    try {
      const res = await prospectsApi.convert(id, convertTo);
      toast.success(`تم ترحيل "${res.data.name}" إلى ${convertTo === "client" ? "العملاء" : "المسوقين"} ✅`);
      setConvertingId(null); load(); loadStats();
    } catch { toast.error("فشل الترحيل"); }
  };

  const exportCSV = () => {
    const header = "الاسم,الرقم,رقم2,المصدر,تصنيف,الحالة,الجروبات,أسماء الجروبات,أسماء النمبربوك,ملاحظات,تاريخ الإضافة\n";
    const esc = (v) => {
      const s = String(v || "");
      if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filteredItems.map(i => [
      i.name, i.phone, i.phone2 || "", i.source || "",
      i.classification || "",
      STATUS_MAP[i.status]?.label || i.status,
      i.group_count || 0,
      (i.group_names || "").replace(/\|\|/g, " ، "),
      (i.nb_names || "").replace(/\|\|/g, ", "),
      i.notes || "",
      new Date(i.created_at).toLocaleDateString("ar-SA"),
    ].map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `المحتملين_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSVLine = (line) => {
    const fields = [];
    let current = "", inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
          else { inQuotes = false; }
        } else current += ch;
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { fields.push(current.trim()); current = ""; }
        else current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  // v3.3.549 — Normalize a phone number cell.
  // Excel/Google Sheets exports long phone numbers in scientific notation
  // ("9.66556E+11"). We detect this and reconstruct the original number using BigInt
  // to avoid float precision loss for 12-digit numbers.
  const normalizePhone = (raw) => {
    if (!raw) return "";
    let s = String(raw).trim();
    if (!s) return "";
    // Detect scientific notation like "9.66556E+11" or "9.66556e+11"
    if (/^-?\d+(\.\d+)?[eE][+-]?\d+$/.test(s)) {
      try {
        // Use BigInt-via-string approach to keep full precision
        // Parse manually to avoid JS float loss on >15 digit numbers
        const match = s.match(/^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
        if (match) {
          const sign = match[1];
          const intPart = match[2];
          const fracPart = match[3] || "";
          const exp = parseInt(match[4]);
          // Combined digits = intPart + fracPart, decimal-point position = intPart.length + exp
          const digits = intPart + fracPart;
          const targetDecimalPos = intPart.length + exp;
          if (targetDecimalPos >= digits.length) {
            // Pad with zeros on the right
            s = sign + digits + "0".repeat(targetDecimalPos - digits.length);
          } else if (targetDecimalPos > 0) {
            // Truncate fractional part (phone numbers are integers)
            s = sign + digits.slice(0, targetDecimalPos);
          } else {
            s = sign + "0";
          }
        }
      } catch (_) {
        // Fallback: leave as-is
      }
    }
    // Strip any non-digit characters except leading +
    const hasPlus = s.startsWith("+");
    s = s.replace(/\D/g, "");
    return hasPlus ? "+" + s : s;
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const importToast = toast.loading("جاري قراءة الملف...");
    try {
      const text = await file.text();
      // Strip BOM if present (UTF-8 with BOM is common from Excel)
      const cleanText = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
      const lines = cleanText.split("\n").filter(l => l.trim());
      if (lines.length < 2) {
        toast.dismiss(importToast);
        return toast.error("الملف فارغ");
      }
      const rows = [];
      let skippedRows = 0;
      let scientificNotationCount = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i].replace(/\r$/, ""));
        // v3.3.549 — Detect scientific notation BEFORE normalizing (so we can warn the user)
        if (/[eE][+-]?\d+/.test(cols[1] || "")) scientificNotationCount++;
        const phone = normalizePhone(cols[1]);
        const phone2 = normalizePhone(cols[2]);
        if (!cols[0] && !phone) { skippedRows++; continue; }
        rows.push({
          name: (cols[0] || "").trim(),
          phone,
          phone2,
          source: (cols[3] || "").trim(),
          notes: (cols[8] || "").trim(),
          nb_names: (cols[7] || "").replace(/,\s*/g, " || "),
        });
      }
      if (rows.length === 0) {
        toast.dismiss(importToast);
        return toast.error("لا توجد بيانات صالحة");
      }

      // v3.3.549 — Critical warning: Excel corrupted the phone numbers
      if (scientificNotationCount > rows.length / 2) {
        toast.dismiss(importToast);
        const proceed = window.confirm(
          `⚠️ تحذير حرج: تم اكتشاف ${scientificNotationCount} رقم هاتف بصيغة علمية (مثل "9.66556E+11").\n\n` +
          `هذا يعني أن Excel/Sheets قام بفقد دقة الأرقام عند التصدير، وأرقام الهواتف الحقيقية ضاعت!\n\n` +
          `الحل الصحيح:\n` +
          `1. افتح الملف الأصلي في Excel\n` +
          `2. حدّد عمود "الرقم" → كليك يمين → Format Cells → Text\n` +
          `3. أعد إدخال الأرقام أو احصل عليها من المصدر الأصلي\n` +
          `4. صدّر كـ CSV مع الاحتفاظ بالأرقام كنصوص\n\n` +
          `هل تريد الاستمرار رغم ذلك؟ (الأرقام ستكون ناقصة)`
        );
        if (!proceed) {
          if (fileRef.current) fileRef.current.value = "";
          return;
        }
      }

      // v3.3.549 — Batch large imports to avoid payload-too-large + give progress feedback
      const BATCH_SIZE = 1000;
      let totalAdded = 0, totalUpdated = 0, totalSkipped = skippedRows;
      let batchErrors = 0;
      const totalBatches = Math.ceil(rows.length / BATCH_SIZE);

      for (let b = 0; b < totalBatches; b++) {
        const batch = rows.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
        toast.loading(`جاري الاستيراد... (${b + 1}/${totalBatches})`, { id: importToast });
        try {
          const res = await prospectsApi.bulkAdd({ rows: batch });
          const d = res.data;
          totalAdded += d.added || 0;
          totalUpdated += d.updated || 0;
          totalSkipped += d.skipped || 0;
        } catch (err) {
          batchErrors++;
          console.error(`Batch ${b + 1} failed:`, err);
        }
      }

      toast.dismiss(importToast);
      const parts = [];
      if (totalAdded) parts.push(`${totalAdded} جديد`);
      if (totalUpdated) parts.push(`${totalUpdated} محدّث`);
      if (totalSkipped) parts.push(`${totalSkipped} تخطّي`);
      if (batchErrors > 0) {
        toast.error(`اكتمل جزئياً: ${parts.join(" • ")} • ${batchErrors} دفعات فشلت`, { duration: 8000 });
      } else if (parts.length === 0) {
        toast.error("لم يتم استيراد أي صف. تحقق من تنسيق الملف.");
      } else {
        toast.success(`تم الاستيراد: ${parts.join(" • ")} ✅`, { duration: 6000 });
      }
      load(); loadSources(); loadStats();
    } catch (err) {
      toast.dismiss(importToast);
      const msg = err?.response?.data?.error || err?.message || "خطأ غير معروف";
      toast.error(`فشل الاستيراد: ${msg}`, { duration: 8000 });
      console.error("Import error:", err);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  // ─── Column show/hide (v3.3.601) ───
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { const s = window.localStorage?.getItem("prospectsHiddenCols"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => { try { window.localStorage?.setItem("prospectsHiddenCols", JSON.stringify(hiddenCols)); } catch {} }, [hiddenCols]);

  // ─── Column config ───
  const COLS = [
    { key: "__select__", label: "", sortable: false, filterable: false, w: 36 },
    { key: "__idx__", label: "#", sortable: false, filterable: false, w: 45 },
    { key: "name", label: "الاسم", sortable: true, filterable: true },
    { key: "phone", label: "الرقم", sortable: true, filterable: true },
    { key: "phone2", label: "رقم 2", sortable: true, filterable: true },
    { key: "source", label: "المصدر", sortable: true, filterable: true },
    { key: "classification", label: "تصنيف", sortable: true, filterable: true, w: 120 },
    { key: "status", label: "الحالة", sortable: true, filterable: true },
    { key: "group_count", label: "الجروبات", sortable: true, filterable: true, w: 80 },
    { key: "group_names", label: "أسماء الجروبات", sortable: false, filterable: true },
    // v3.3.550 — Membership in prospect_groups (managed in /prospect-groups page)
    { key: "my_groups", label: "مجموعات المحتملين", sortable: false, filterable: true, w: 200 },
    { key: "nb_names", label: "أسماء النمبربوك", sortable: false, filterable: true },
    { key: "notes", label: "ملاحظات", sortable: true, filterable: true },
    { key: "created_at", label: "تاريخ الإضافة", sortable: true, filterable: true, w: 120 },
    { key: "added_by_name", label: "أضيف بواسطة", sortable: true, filterable: true },
    { key: "__actions__", label: "إجراءات", sortable: false, filterable: false, w: 120 },
  ];

  // v3.3.601 — show/hide columns (same UX as the reservations grid). Structural
  // columns (select/index/actions) are always visible. Hidden columns are
  // collapsed via a per-column CSS class so header + body stay aligned.
  const STRUCTURAL_COLS = ["__select__", "__idx__", "__actions__"];
  const toggleableCols = COLS.filter(c => !STRUCTURAL_COLS.includes(c.key));
  const toggleCol = (key) => setHiddenCols(h => h.includes(key) ? h.filter(k => k !== key) : [...h, key]);
  const colHiddenCss = hiddenCols.map(k => `.prospects-tbl .pcol-${k}{display:none;}`).join("");

  return (
    <div style={{ padding: "20px 28px", direction: "rtl", fontFamily: "'Segoe UI', Tahoma, sans-serif" }}>
      {/* v3.4.278 — تابان: القائمة / التحليل */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 14 }}>
        {[["list", "📋 قائمة المسوقين المحتملين"], ["analysis", "📊 تحليل قائمة المسوقين المحتملين"]].map(([k, l]) => (
          <button key={k} onClick={() => setPageTab(k)} style={{ padding: "9px 16px", background: "none", border: "none", borderBottom: pageTab === k ? "3px solid #2563eb" : "3px solid transparent", marginBottom: -2, color: pageTab === k ? "#1d4ed8" : "#64748b", fontWeight: pageTab === k ? 800 : 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
        ))}
      </div>
      {pageTab === "analysis" && <ProspectsAnalysis onOpen={(phone) => { setGlobalSearch(phone); setPageTab("list"); }} />}
      <div style={{ display: pageTab === "list" ? "block" : "none" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1e293b" }}>المحتملين</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>عملاء ومسوقين محتملين — يمكن ترحيلهم للقوائم الفعلية</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <ColumnToggle columns={toggleableCols} hiddenCols={hiddenCols} onToggle={toggleCol} />
          {canAdd && <button onClick={() => setShowForm(!showForm)} style={btnS("#2563eb", "#fff")}><FiPlus size={14} /> إضافة</button>}
          {canExport && <button onClick={exportCSV} style={btnS("#fff", "#374151", "1px solid #d1d5db")}><FiDownload size={13} /> تصدير</button>}
          {canImport && (
            <label style={{ ...btnS("#fff", "#374151", "1px solid #d1d5db"), cursor: "pointer" }}>
              <FiUpload size={13} /> استيراد
              <input type="file" accept=".csv" ref={fileRef} onChange={handleImport} style={{ display: "none" }} />
            </label>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: "الإجمالي", value: stats.total, color: "#3b82f6" },
            { label: "جديد", value: stats.new_count, color: "#3b82f6" },
            { label: "تم التواصل", value: stats.contacted, color: "#f59e0b" },
            { label: "تم الترحيل", value: stats.converted, color: "#10b981" },
            { label: "مرفوض", value: stats.rejected, color: "#ef4444" },
            { label: "المصادر", value: stats.sources_count, color: "#8b5cf6" },
          ].map((s, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 16px", minWidth: 90, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{Number(s.value || 0).toLocaleString("en-US")}</div>
              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>إضافة محتمل جديد</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>الاسم</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputS} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>الرقم</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ ...inputS, direction: "ltr" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>رقم 2</label>
              <input value={form.phone2} onChange={e => setForm(f => ({ ...f, phone2: e.target.value }))} style={{ ...inputS, direction: "ltr" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>المصدر</label>
              <input value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} placeholder="واتساب، معرض، إعلان..." style={inputS} list="source-list" />
              <datalist id="source-list">{sources.map((s, i) => <option key={i} value={s} />)}</datalist>
            </div>
            {/* v3.3.606 — تصنيف المحتمل */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>تصنيف</label>
              <select value={form.classification} onChange={e => setForm(f => ({ ...f, classification: e.target.value }))} style={inputS}>
                <option value="">— اختر —</option>
                {CLASSIFICATIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>ملاحظات</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={inputS} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setShowForm(false)} style={btnS("#fff", "#374151", "1px solid #d1d5db")}><FiX size={13} /> إلغاء</button>
            <button onClick={handleAdd} style={btnS("#2563eb", "#fff")}><FiCheck size={13} /> حفظ</button>
          </div>
        </div>
      )}

      {/* Global Search + Bulk Actions */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 350 }}>
          <FiSearch style={{ position: "absolute", right: 10, top: 9, color: "#94a3b8" }} />
          <input value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} placeholder="بحث شامل في كل الأعمدة..."
            style={{ ...inputS, paddingRight: 34 }} />
        </div>
        {hasActiveFilters && (
          <button onClick={clearAllFilters} style={{ ...btnS("#fee2e2", "#dc2626", "1px solid #fecaca"), fontSize: 11 }}>
            <FiX size={12} /> مسح الفلاتر
          </button>
        )}
        {selectedIds.size > 0 && (
          <>
            {/* v3.3.550 — Bulk add selected prospects to a prospect group */}
            <button onClick={() => setShowAddToGroupModal(true)} style={btnS("#7c3aed", "#fff")}>
              <FiUsers size={13} /> إضافة لمجموعة ({selectedIds.size})
            </button>
            <button onClick={handleBulkDelete} style={btnS("#dc2626", "#fff")}>
              <FiTrash2 size={13} /> حذف المحدد ({selectedIds.size})
            </button>
            <button onClick={clearSelection} style={btnS("#fff", "#374151", "1px solid #d1d5db")}>
              <FiX size={12} /> إلغاء التحديد
            </button>
          </>
        )}
        <span style={{ fontSize: 12, color: "#94a3b8", marginRight: "auto" }}>
          {filteredCount === total ? `${total} محتمل` : `${filteredCount} من ${total} محتمل`}
          {selectedIds.size > 0 && <span style={{ color: "#2563eb", fontWeight: 600 }}> • {selectedIds.size} محدد</span>}
        </span>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "visible" }}>
        <div style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto", overflowX: "auto" }}>
          {colHiddenCss && <style>{colHiddenCss}</style>}
          <table className="prospects-tbl" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1450 }}>
            <thead>
              <tr>
                {COLS.map(col => {
                  if (col.key === "__select__") {
                    return (
                      <th key={col.key} className={"pcol-" + col.key} style={{ ...thS, width: col.w, textAlign: "center", padding: "6px 4px" }}>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={el => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                          onChange={toggleSelectAllVisible}
                          style={{ cursor: "pointer" }}
                          title="تحديد كل الظاهر"
                        />
                      </th>
                    );
                  }
                  if (!col.filterable) {
                    return <th key={col.key} className={"pcol-" + col.key} style={{ ...thS, width: col.w || "auto" }}>{col.label}</th>;
                  }
                  const isActive = colHasFilter(col.key);
                  const isOpen = openFilterCol === col.key;
                  return (
                    <th key={col.key} className={"pcol-" + col.key} style={{ ...thS, width: col.w || "auto", position: "relative" }}>
                      <div
                        onClick={() => setOpenFilterCol(isOpen ? null : col.key)}
                        style={{
                          display: "flex", alignItems: "center", gap: 4, cursor: "pointer",
                          padding: "2px 4px", borderRadius: 4,
                          background: isActive ? "#dbeafe" : "transparent",
                          color: isActive ? "#1d4ed8" : "#64748b",
                        }}
                      >
                        {col.sortable && <SortIcon sortKey={sortKey} sortDir={sortDir} colKey={col.key} />}
                        <span style={{ flex: 1 }}>{col.label}</span>
                        <FiFilter size={10} style={{ color: isActive ? "#1d4ed8" : "#cbd5e1" }} />
                      </div>
                      {isOpen && (
                        <ColumnFilterDropdown
                          colKey={col.key}
                          label={col.label}
                          values={getUniqueValuesForColumn(col.key)}
                          selected={columnFilters[col.key] || new Set()}
                          onChange={(newSel) => setColumnFilters(prev => {
                            const next = { ...prev };
                            if (!newSel || newSel.size === 0) delete next[col.key];
                            else next[col.key] = newSel;
                            return next;
                          })}
                          onClose={() => setOpenFilterCol(null)}
                          onSort={handleSort}
                          sortKey={sortKey}
                          sortDir={sortDir}
                          canSort={col.sortable}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLS.length} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>جاري التحميل...</td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><td colSpan={COLS.length} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
                  {hasActiveFilters ? "لا توجد نتائج مطابقة للفلتر" : "لا يوجد محتملين — اضغط \"إضافة\" أو \"استيراد\""}
                </td></tr>
              ) : pagedItems.map((item, idx0) => { const idx = idx0 + pageOffset; // v3.4.190
                const isSelected = selectedIds.has(item.id);
                return (
                <tr key={item.id} onDoubleClick={() => startEdit(item)} title="انقر مرتين للتعديل"
                  style={{ background: isSelected ? "#fef9c3" : (item.status === "converted" ? "#f0fdf4" : "#fff"), cursor: "default" }}>
                  <td style={{ ...tdS, textAlign: "center", padding: "6px 4px" }} onDoubleClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelectOne(item.id)} style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ ...tdS, color: "#94a3b8", fontSize: 11 }}>{idx + 1}</td>

                  <td className="pcol-name" style={tdS}>
                    <span style={{ fontWeight: 600, color: "#1e293b" }}>{item.name || "—"}</span>
                  </td>

                  <td className="pcol-phone" style={{ ...tdS, direction: "ltr", textAlign: "center", fontFamily: "monospace", fontSize: 12 }}>
                    {item.phone || "—"}
                  </td>

                  <td className="pcol-phone2" style={{ ...tdS, direction: "ltr", textAlign: "center", fontFamily: "monospace", fontSize: 12 }}>
                    {item.phone2 || "—"}
                  </td>

                  <td className="pcol-source" style={tdS}>
                    {item.source ? (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#ede9fe", color: "#5b21b6", fontWeight: 600 }}>{item.source}</span>
                    ) : "—"}
                  </td>

                  {/* v3.3.606 — تصنيف */}
                  <td className="pcol-classification" style={tdS}>
                    {item.classification ? (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                        background: (CLASSIFICATION_STYLE[item.classification] || {}).bg || "#f1f5f9",
                        color: (CLASSIFICATION_STYLE[item.classification] || {}).color || "#475569" }}>
                        {item.classification}
                      </span>
                    ) : "—"}
                  </td>

                  <td className="pcol-status" style={tdS}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: STATUS_MAP[item.status]?.bg || "#f3f4f6", color: STATUS_MAP[item.status]?.color || "#374151", fontWeight: 600 }}>
                      {STATUS_MAP[item.status]?.label || item.status}
                    </span>
                  </td>

                  <td className="pcol-group_count" style={{ ...tdS, textAlign: "center", fontSize: 12 }}>
                    {item.group_count > 0 ? (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#dbeafe", color: "#1e40af", fontWeight: 700 }}>{item.group_count}</span>
                    ) : "—"}
                  </td>

                  <td className="pcol-group_names" style={{ ...tdS, fontSize: 11, color: "#6b7280", maxWidth: 200 }}>
                    {item.group_names ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                        {item.group_names.split("||").filter(Boolean).map((g, gi) => (
                          <span key={gi} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 6, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", whiteSpace: "nowrap" }}>{g.trim()}</span>
                        ))}
                      </div>
                    ) : "—"}
                  </td>

                  {/* v3.3.550 — مجموعات المحتملين (prospect_groups membership) */}
                  <td className="pcol-my_groups" style={{ ...tdS, fontSize: 11, color: "#6b7280", maxWidth: 220 }}>
                    {Array.isArray(item.my_groups) && item.my_groups.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                        {item.my_groups.map((g) => (
                          <span key={g.id} title={g.name} style={{
                            fontSize: 10, padding: "1px 7px", borderRadius: 10,
                            background: "#ede9fe", color: "#6d28d9",
                            border: "1px solid #ddd6fe", whiteSpace: "nowrap",
                            fontWeight: 600,
                          }}>📁 {g.name}</span>
                        ))}
                      </div>
                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>

                  <td className="pcol-nb_names" style={{ ...tdS, fontSize: 11, color: "#475569", maxWidth: 180, minWidth: 120 }}>
                    {item.nb_names ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {item.nb_names.split("||").filter(Boolean).map((n, ni) => (
                          <span key={ni} style={{ fontSize: 11, padding: "1px 6px", borderRadius: 4, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", whiteSpace: "nowrap", display: "inline-block" }}>{n.trim()}</span>
                        ))}
                      </div>
                    ) : "—"}
                  </td>

                  {/* v3.3.614 — ملاحظات تراكمية: متعددة فوق بعض + إضافة عبر بوب-أب */}
                  <td className="pcol-notes" style={{ ...tdS, maxWidth: 250, minWidth: 160, verticalAlign: "top" }}>
                    <ProspectNotesCell prospect={item} />
                  </td>

                  <td className="pcol-created_at" style={{ ...tdS, fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {new Date(item.created_at).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" })}
                  </td>

                  <td className="pcol-added_by_name" style={{ ...tdS, fontSize: 11, color: "#94a3b8" }}>{item.added_by_name || "—"}</td>

                  <td style={{ ...tdS, whiteSpace: "nowrap" }} onDoubleClick={e => e.stopPropagation()}>
                    {convertingId === item.id ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => handleConvert(item.id, "client")} style={{ ...btnS("#dbeafe", "#1e40af"), padding: "4px 8px", fontSize: 11 }}>
                          <FiUserPlus size={11} /> عميل
                        </button>
                        <button onClick={() => handleConvert(item.id, "marketer")} style={{ ...btnS("#ede9fe", "#5b21b6"), padding: "4px 8px", fontSize: 11 }}>
                          <FiUserPlus size={11} /> مسوق
                        </button>
                        <button onClick={() => setConvertingId(null)} style={{ ...btnS("#f3f4f6", "#6b7280"), padding: "4px 8px" }}><FiX size={11} /></button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 4 }}>
                        {item.status !== "converted" && (
                          <button onClick={() => setConvertingId(item.id)} title="ترحيل" style={{ background: "none", border: "none", cursor: "pointer", color: "#10b981", padding: 4 }}>
                            <FiArrowRight size={14} />
                          </button>
                        )}
                        <button onClick={() => startEdit(item)} title="تعديل" style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: 4 }}>
                          <FiEdit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(item.id)} title="حذف" style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 4 }}>
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    )}
                    {item.status === "converted" && (
                      <div style={{ fontSize: 10, color: "#10b981", marginTop: 2 }}>
                        → {item.converted_to === "client" ? "عميل" : "مسوق"} #{item.converted_id}
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "8px 16px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            {hasActiveFilters ? `${filteredCount} نتيجة من ${total} محتمل` : `إجمالي: ${total} محتمل`}
            {selectedIds.size > 0 && <span style={{ color: "#2563eb", fontWeight: 600 }}> • {selectedIds.size} محدد</span>}
          </span>
          {/* v3.4.190 — ترقيم الصفحات */}
          {pageCount > 1 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
              <button onClick={() => setPageNo(p => Math.max(1, p - 1))} disabled={pageNo <= 1} style={{ padding: "3px 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: pageNo <= 1 ? 0.5 : 1 }}>السابق</button>
              <span style={{ color: "#475569" }}>{pageOffset + 1}–{Math.min(pageOffset + PAGE, filteredCount)} من {filteredCount} · صفحة {Math.min(pageNo, pageCount)}/{pageCount}</span>
              <button onClick={() => setPageNo(p => Math.min(pageCount, p + 1))} disabled={pageNo >= pageCount} style={{ padding: "3px 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: pageNo >= pageCount ? 0.5 : 1 }}>التالي</button>
            </span>
          )}
          {hasActiveFilters && (
            <button onClick={clearAllFilters} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
              مسح كل الفلاتر
            </button>
          )}
        </div>
      </div>

      {/* v3.3.607 — Edit popup (opened via row double-click or the ✏️ action). Notes are NOT here — edited inline via "+". */}
      {editId && (
        <div onClick={() => setEditId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(94vw, 540px)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 50px rgba(0,0,0,0.2)", direction: "rtl" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>✏️ تعديل المحتمل</h3>
              <button onClick={() => setEditId(null)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#64748b" }}><FiX size={18} /></button>
            </div>
            <div style={{ padding: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>الاسم</label>
                <input value={editData.name || ""} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} style={inputS} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>الرقم</label>
                <input value={editData.phone || ""} onChange={e => setEditData(d => ({ ...d, phone: e.target.value }))} style={{ ...inputS, direction: "ltr", textAlign: "right" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>رقم 2</label>
                <input value={editData.phone2 || ""} onChange={e => setEditData(d => ({ ...d, phone2: e.target.value }))} style={{ ...inputS, direction: "ltr", textAlign: "right" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>المصدر</label>
                <input value={editData.source || ""} onChange={e => setEditData(d => ({ ...d, source: e.target.value }))} style={inputS} list="source-list-modal" />
                <datalist id="source-list-modal">{sources.map((s, i) => <option key={i} value={s} />)}</datalist>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>تصنيف</label>
                <select value={editData.classification || ""} onChange={e => setEditData(d => ({ ...d, classification: e.target.value }))} style={inputS}>
                  <option value="">— اختر —</option>
                  {CLASSIFICATIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>الحالة</label>
                <select value={editData.status || "new"} onChange={e => setEditData(d => ({ ...d, status: e.target.value }))} style={inputS}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>أسماء النمبربوك (افصل بـ ||)</label>
                <input value={editData.nb_names || ""} onChange={e => setEditData(d => ({ ...d, nb_names: e.target.value }))} placeholder="اسم1 || اسم2 || ..." style={inputS} />
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setEditId(null)} style={btnS("#fff", "#374151", "1px solid #d1d5db")}><FiX size={13} /> إلغاء</button>
              <button onClick={saveEdit} style={btnS("#2563eb", "#fff")}><FiCheck size={13} /> حفظ</button>
            </div>
          </div>
        </div>
      )}

      {/* v3.3.550 — Add selected prospects to a prospect group modal */}
      {showAddToGroupModal && (
        <AddToGroupModal
          selectedCount={selectedIds.size}
          loading={loadingGroups}
          groups={availableGroups}
          busy={addToGroupBusy}
          onLoad={async () => {
            setLoadingGroups(true);
            try {
              const r = await prospectGroupsApi.list();
              setAvailableGroups(r.data?.groups || r.data || []);
            } catch (e) {
              toast.error("فشل تحميل المجموعات");
            } finally {
              setLoadingGroups(false);
            }
          }}
          onClose={() => setShowAddToGroupModal(false)}
          onConfirm={async (groupId) => {
            if (!groupId) return;
            setAddToGroupBusy(true);
            try {
              const r = await prospectGroupsApi.addMembers(groupId, Array.from(selectedIds));
              const d = r.data || {};
              const parts = [];
              if (d.added) parts.push(`${d.added} أُضيف`);
              if (d.skipped) parts.push(`${d.skipped} موجود مسبقاً`);
              toast.success(`تم: ${parts.join(" • ") || "لا تغييرات"} ✅`, { duration: 5000 });
              setShowAddToGroupModal(false);
              clearSelection();
              load();
            } catch (e) {
              toast.error(e?.response?.data?.error || "فشلت العملية");
            } finally {
              setAddToGroupBusy(false);
            }
          }}
        />
      )}
      </div>{/* v3.4.278 — نهاية غلاف القائمة */}
    </div>
  );
}

// ─── v3.3.614 — خلية ملاحظات المحتمل التراكمية (ملاحظات متعددة فوق بعض + إضافة عبر بوب-أب) ───
function ProspectNotesCell({ prospect }) {
  const [notes, setNotes] = useState(prospect.notes_log || []);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNotes(prospect.notes_log || []); }, [prospect.notes_log]);

  const addNote = async () => {
    const t = draft.trim();
    if (!t) return;
    setSaving(true);
    try {
      const r = await prospectsApi.addNote(prospect.id, t);
      const n = r.data.note;
      setNotes(prev => [{ id: n.id, text: n.note_text, user: n.user_name, date: n.created_at }, ...prev]);
      setDraft(""); setShowAdd(false);
    } catch { toast.error("فشل حفظ الملاحظة"); }
    finally { setSaving(false); }
  };
  const delNote = async (noteId) => {
    try { await prospectsApi.deleteNote(prospect.id, noteId); setNotes(prev => prev.filter(n => n.id !== noteId)); }
    catch { toast.error("فشل حذف الملاحظة"); }
  };

  return (
    <div onDoubleClick={e => e.stopPropagation()}>
      {notes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 96, overflowY: "auto", marginBottom: 4 }}>
          {notes.map(n => (
            <div key={n.id} style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "3px 6px" }}>
              <div style={{ color: "#78350f", fontSize: 11, whiteSpace: "pre-wrap" }}>{n.text}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2, fontSize: 9, color: "#a16207" }}>
                <span>{n.user || ""}{n.date ? " · " + new Date(n.date).toLocaleDateString("ar-SA") : ""}</span>
                <button onClick={() => delNote(n.id)} title="حذف" style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 10, padding: 0 }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => { setShowAdd(true); setDraft(""); }} title="إضافة ملاحظة"
        style={{ border: "1px dashed #93c5fd", background: "#eff6ff", color: "#2563eb", borderRadius: 6, padding: "2px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
        + ملاحظة
      </button>

      {showAdd && (
        <div onClick={() => setShowAdd(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(92vw, 460px)", boxShadow: "0 20px 50px rgba(0,0,0,0.2)", direction: "rtl" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1e293b" }}>📝 إضافة ملاحظة — {prospect.name || ""}</h3>
              <button onClick={() => setShowAdd(false)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#64748b" }}><FiX size={18} /></button>
            </div>
            <div style={{ padding: 16 }}>
              <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} placeholder="اكتب الملاحظة..." rows={4}
                onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) addNote(); }}
                style={{ width: "100%", padding: 10, border: "1px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit", fontSize: 13, boxSizing: "border-box", resize: "vertical" }} />
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAdd(false)} style={btnS("#fff", "#374151", "1px solid #d1d5db")}><FiX size={13} /> إلغاء</button>
              <button onClick={addNote} disabled={saving || !draft.trim()} style={btnS("#2563eb", "#fff")}><FiCheck size={13} /> {saving ? "..." : "حفظ"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── v3.3.550 — Modal: Add selected prospects to a prospect group ───
function AddToGroupModal({ selectedCount, loading, groups, busy, onLoad, onClose, onConfirm }) {
  const [selectedGroupId, setSelectedGroupId] = useState("");

  useEffect(() => { onLoad(); /* eslint-disable-next-line */ }, []);

  return (
    <div onClick={busy ? null : onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)",
      zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} dir="rtl" style={{
        background: "#fff", borderRadius: 12, padding: 24, maxWidth: 500, width: "100%",
        boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
              <FiUsers size={16} style={{ color: "#7c3aed" }} /> إضافة إلى مجموعة محتملين
            </h3>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              ستتم إضافة <strong>{selectedCount}</strong> محتمل محدّد
            </div>
          </div>
          <button onClick={onClose} disabled={busy} style={{
            background: "none", border: "none", cursor: busy ? "not-allowed" : "pointer",
            color: "#64748b", fontSize: 18,
          }}>
            <FiX />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
            جاري تحميل المجموعات...
          </div>
        ) : groups.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 13, color: "#475569" }}>لا توجد مجموعات محتملين بعد</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              أنشئ مجموعة أولاً من صفحة "مجموعات المحتملين"
            </div>
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
              اختر المجموعة:
            </label>
            <select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)} disabled={busy}
              style={{
                width: "100%", padding: "10px 12px",
                border: "1px solid #e2e8f0", borderRadius: 8,
                fontFamily: "inherit", fontSize: 13, marginBottom: 16,
              }}>
              <option value="">-- اختر مجموعة --</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name} {g.member_count != null ? `(${g.member_count} عضو)` : ""}
                </option>
              ))}
            </select>

            <div style={{
              fontSize: 10, color: "#64748b", marginBottom: 16,
              background: "#f1f5f9", padding: 10, borderRadius: 6,
            }}>
              💡 المحتملون الموجودون في المجموعة مسبقاً سيُتخطّون تلقائياً.
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={onClose} disabled={busy} style={{
                padding: "8px 16px", background: "#f1f5f9", color: "#475569",
                border: "none", borderRadius: 6, fontSize: 12, cursor: busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}>إلغاء</button>
              <button onClick={() => onConfirm(selectedGroupId)} disabled={busy || !selectedGroupId} style={{
                padding: "8px 18px",
                background: (busy || !selectedGroupId) ? "#cbd5e1" : "#7c3aed",
                color: "#fff", border: "none", borderRadius: 6,
                fontSize: 12, fontWeight: 600,
                cursor: (busy || !selectedGroupId) ? "not-allowed" : "pointer",
                fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
              }}>
                {busy ? "جاري الإضافة..." : <><FiPlus size={12} /> إضافة</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
